import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

// Bond Calculator — 2 sections: Bond Price at Coupon Date + Bond Pricing between Coupon Dates
export default function BondCalc() {
  return (
    <CalcShell look="cnet" title="Bond Calculator" subtitle="Compute bond price on a coupon date or between coupon dates (dirty vs clean price)."
      about={<><p>A <b>bond</b> is a debt security that pays periodic coupon interest and returns face value at maturity. Its price depends on the yield to maturity, remaining coupons, and time since the last coupon.</p></>}
    >
      <BondOnCoupon/>
      <hr className="my-8 border-slate-200"/>
      <BondBetweenCoupons/>
    </CalcShell>
  );
}
function BondOnCoupon() {
  const [solveFor, setSolveFor] = useState('price'); // 'price' | 'yield'
  const [face, setFace] = useState(100);
  const [yieldPct, setYieldPct] = useState(6);
  const [priceInput, setPriceInput] = useState(97.30);
  const [years, setYears] = useState(3);
  const [couponRate, setCouponRate] = useState(5);
  const [couponUnit, setCouponUnit] = useState('%');
  const [freq, setFreq] = useState(1); // annually
  const c = useMemo(() => {
    const cAmt = couponUnit === '%' ? face * couponRate/100 : couponRate;
    const perCoupon = cAmt / freq;
    if (solveFor === 'price') {
      const y = yieldPct/100/freq;
      const n = years * freq;
      let pv = 0;
      for (let i = 1; i <= n; i++) pv += perCoupon / Math.pow(1+y, i);
      pv += face / Math.pow(1+y, n);
      return { price: pv, yield: yieldPct, perCoupon };
    } else {
      // Solve yield via bisection
      const n = years * freq;
      const priceOfY = (annualY) => {
        const y = annualY/100/freq;
        let pv = 0;
        for (let i = 1; i <= n; i++) pv += perCoupon / Math.pow(1+y, i);
        pv += face / Math.pow(1+y, n);
        return pv;
      };
      let lo = -50, hi = 100;
      for (let i = 0; i < 80; i++) {
        const mid = (lo + hi) / 2;
        if (priceOfY(mid) > priceInput) lo = mid; else hi = mid;
      }
      return { price: priceInput, yield: (lo + hi) / 2, perCoupon };
    }
  }, [face, yieldPct, priceInput, years, couponRate, couponUnit, freq, solveFor]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Bond Price Calculator (at Coupon Date)</h3>
      <div className="flex gap-2 mb-4 text-sm">
        <button onClick={()=>setSolveFor('price')} data-testid="bond-solve-price" className={`px-3 py-1 rounded ${solveFor==='price'?'bg-blue-700 text-white':'bg-slate-100 text-slate-700'}`}>Solve for Price</button>
        <button onClick={()=>setSolveFor('yield')} data-testid="bond-solve-yield" className={`px-3 py-1 rounded ${solveFor==='yield'?'bg-blue-700 text-white':'bg-slate-100 text-slate-700'}`}>Solve for Yield</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Face value</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={face} onChange={e=>setFace(+e.target.value||0)} className="flex-1"/></div></div>
          {solveFor === 'price' ? (
            <div className="field"><label>Yield</label><div className="flex gap-2"><input type="number" step="0.01" value={yieldPct} onChange={e=>setYieldPct(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          ) : (
            <div className="field"><label>Price</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" step="0.01" value={priceInput} onChange={e=>setPriceInput(+e.target.value||0)} className="flex-1" data-testid="bond-price-input"/></div></div>
          )}
          <div className="field"><label>Time to maturity</label><div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
          <div className="field"><label>Annual coupon</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" step="0.01" value={couponRate} onChange={e=>setCouponRate(+e.target.value||0)}/>
              <select value={couponUnit} onChange={e=>setCouponUnit(e.target.value)}><option value="%">%</option><option value="$">$</option></select>
            </div>
          </div>
          <div className="field"><label>Coupon frequency</label>
            <select value={freq} onChange={e=>setFreq(+e.target.value)}><option value={1}>annually</option><option value={2}>semi-annually</option><option value={4}>quarterly</option><option value={12}>monthly</option></select>
          </div>
        </div>
        <div>
          {solveFor === 'price' ? (
            <>
              <div className="result-panel"><div className="result-title">Price</div><div className="result-value" data-testid="bond-price">${c.price.toFixed(4)}</div></div>
              <p className="text-sm text-slate-600 mt-3">Given the face value, yield, time to maturity, and annual coupon, the price is <b>${c.price.toFixed(4)}</b>.</p>
            </>
          ) : (
            <>
              <div className="result-panel"><div className="result-title">Yield</div><div className="result-value" data-testid="bond-yield">{c.yield.toFixed(4)}%</div></div>
              <p className="text-sm text-slate-600 mt-3">Given the face value, price, time to maturity, and annual coupon, the yield is <b>{c.yield.toFixed(4)}%</b>.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
function BondBetweenCoupons() {
  const [face, setFace] = useState(100);
  const [yieldPct, setYieldPct] = useState(6);
  const [couponRate, setCouponRate] = useState(5);
  const [couponUnit, setCouponUnit] = useState('%');
  const [freq, setFreq] = useState(1);
  const [maturity, setMaturity] = useState('2029-07-18');
  const [settlement, setSettlement] = useState('2026-07-22');
  const [dayCount, setDayCount] = useState('30/360');
  const c = useMemo(() => {
    const cAmt = couponUnit === '%' ? face * couponRate/100 : couponRate;
    const perCoupon = cAmt / freq;
    const y = yieldPct/100/freq;
    const mat = new Date(maturity);
    const sett = new Date(settlement);
    const daysPer = 360 / freq;
    const daysSinceCoupon = Math.abs((sett - mat) / (1000 * 60 * 60 * 24)) % daysPer;
    // number of coupons remaining
    const daysToMaturity = (mat - sett) / (1000*60*60*24);
    const n = Math.ceil(daysToMaturity / daysPer);
    let denom;
    if (dayCount === '30/360') denom = 360/freq;
    else if (dayCount === 'Actual/360') denom = 360/freq;
    else if (dayCount === 'Actual/365') denom = 365/freq;
    else denom = 365/freq;
    const w = daysSinceCoupon / denom;
    let dirty = 0;
    for (let i = 0; i < n; i++) dirty += perCoupon / Math.pow(1+y, i + 1 - w);
    dirty += face / Math.pow(1+y, n - w);
    const accrued = perCoupon * w;
    const clean = dirty - accrued;
    return { dirty, clean, accrued, days: Math.round(daysSinceCoupon), n, perCoupon };
  }, [face, yieldPct, couponRate, couponUnit, freq, maturity, settlement, dayCount]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Bond Pricing Calculator (not at Coupon Date)</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Face value</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={face} onChange={e=>setFace(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Yield</label><div className="flex gap-2"><input type="number" step="0.01" value={yieldPct} onChange={e=>setYieldPct(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Annual coupon</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" step="0.01" value={couponRate} onChange={e=>setCouponRate(+e.target.value||0)}/>
              <select value={couponUnit} onChange={e=>setCouponUnit(e.target.value)}><option value="%">%</option><option value="$">$</option></select>
            </div>
          </div>
          <div className="field"><label>Coupon frequency</label>
            <select value={freq} onChange={e=>setFreq(+e.target.value)}><option value={1}>annually</option><option value={2}>semi-annually</option><option value={4}>quarterly</option></select>
          </div>
          <div className="field"><label>Maturity date</label><input type="date" value={maturity} onChange={e=>setMaturity(e.target.value)}/></div>
          <div className="field"><label>Settlement date</label><input type="date" value={settlement} onChange={e=>setSettlement(e.target.value)}/></div>
          <div className="field"><label>Day-count convention</label>
            <div className="grid grid-cols-2 gap-1 text-sm">
              {['30/360','Actual/360','Actual/365','Actual/Actual'].map(d => (
                <label key={d} className="flex items-center gap-1"><input type="radio" name="dc" checked={dayCount===d} onChange={()=>setDayCount(d)}/>{d}</label>
              ))}
            </div>
          </div>
        </div>
        <div>
          <table className="data-table"><tbody>
            <tr><td>Dirty price</td><td className="text-right font-semibold text-blue-700">${c.dirty.toFixed(4)}</td></tr>
            <tr><td>Clean price</td><td className="text-right font-semibold">${c.clean.toFixed(4)}</td></tr>
            <tr><td>Accrued interest</td><td className="text-right">${c.accrued.toFixed(4)}</td></tr>
            <tr><td>Interest accrued days</td><td className="text-right">{c.days}</td></tr>
            <tr><td>Coupons remaining</td><td className="text-right">{c.n}</td></tr>
            <tr><td>Days between coupons</td><td className="text-right">{Math.round(360/freq)}</td></tr>
            <tr><td>Coupon amount (each)</td><td className="text-right">${c.perCoupon.toFixed(4)}</td></tr>
          </tbody></table>
        </div>
      </div>
    </div>
  );
}
